import { Box, Grid, Stack, styled, Typography } from "@mui/material";
import { BANNER_CONTENT_HEIGHT } from "app/appConstants";
import { BannerContentType, BannerPanoramicOption, BannerResponse, BannerTopicGoToType } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { LmsPaths } from "routes/LmsRouter";
import { AssPlayerSurvey } from "views/assessment/player/AssPlayerSurvey";
import { resolveLinkFeature } from "views/general/Cards/CardDtoActions";

type BannerProps = {
    viewData: BannerResponse;
};

export const BannerRender = ({ viewData }: BannerProps) => {
    const api = useCoreApi();
    const navigate = useNavigate();
    const [dialog, setDialog] = useState<any>(null);
    const [clickBanner, setClickBanner] = useState<boolean>(false);
    const mainContainerRef = useRef<HTMLDivElement>(null);

    ///
    const MainContainer = styled("div")(
        ({ theme }) => ({
            height: viewData.containerHeight,
            width: "100%",
            position: "relative",
            borderRadius: "16px",
            overflow: "hidden"
        })
    );

    ///
    const BackgroundImage = styled(
        "div",
        { shouldForwardProp: (propName: string) => !["imageUrl"].includes(propName) }
    )<{ imageUrl: string; }>(
        ({ theme, imageUrl }) => (
            {
                backgroundImage: `url("${imageUrl}")`,
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "200%",
                filter: "blur(40px)",
                WebkitFilter: "blur(40px)",
                height: "100%",
                width: "100%",
                margin: "16px",
                bottom: 0,
                objectFit: "contain",
            }
        )
    );

    const LateralContainer = styled("div")(
        ({ theme }) => ({
            width: "calc(100% - 16px)",
            height: `calc(100% - ${BANNER_CONTENT_HEIGHT}px)`,
            position: "absolute",
            bottom: "60px",
            top: "16px",
            right: "16px",
            backgroundImage: "unset",
            backgroundColor: "unset",
            zIndex: 1,
            boxShadow: "unset"
        })
    );

    ///
    const ContentContainer = styled("div")(
        ({ theme }) => ({
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            fontWeight: "bold",
            position: "absolute",
            top: `calc(100% - ${BANNER_CONTENT_HEIGHT}px)`,
            height: `${BANNER_CONTENT_HEIGHT}px`,
            zIndex: "0",
            width: "100%",
            borderBottomLeftRadius: "16px",
            borderBottomRightRadius: "16px",
        })
    );

    ///
    const handleClickBanner = () => {

        switch (viewData.contentType) {
            case BannerContentType.Publication:
                let publicationRoute = viewData.publicationType != null ? KnowledgePaths.PublicationViewerResolver(viewData.id!, viewData.publicationType!) : null;
                navigate(publicationRoute!);
                break;
            case BannerContentType.Training:
                navigate(LmsPaths.BuildTrainingViewer(viewData.id!));
                break;
            case BannerContentType.Assessment:
                if (viewData.survey != null)
                    setDialog(
                        <AssPlayerSurvey
                            deployId={viewData.id!}
                            assessmentId={viewData.survey!.assessmentId}
                            mode="play"
                            onClose={(success) => setDialog(false)}
                        />
                    );
                break;
            case BannerContentType.Topic:
                if (viewData.topicGoTo == BannerTopicGoToType.TrainingCatalog) navigate(KnowledgePaths.FeedTopicTrainings(viewData.id!));
                else navigate(KnowledgePaths.FeedTopic(viewData.id!));
                break;
            case BannerContentType.Link:
                if (!viewData.linkOnOpen) window.open(viewData.linkUrl!, '_blank');
                else window.location.href = viewData.linkUrl!;
                break;
            case BannerContentType.System:
                resolveLinkFeature(viewData.systemFeature!, setDialog, navigate);
                break;
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// USEEFFECT
    ////////////////////////////////////////////////////////////////////////////

    /// ao clicar no banner registra o click
    useEffect(() => {
        async function getData() {
            try {
                await await api.v1.banner.addHit({ BannerId: viewData.bannerId });
                handleClickBanner();
            }
            catch {
                setClickBanner(false);
            }
            finally {
                setClickBanner(false);
            }
        }
        if (clickBanner) getData();
    }, [clickBanner]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}

            <MainContainer
                ref={mainContainerRef}
                onClick={(e) => setClickBanner(true)}
                onMouseEnter={
                    () => {
                        if (mainContainerRef.current && viewData.contentType != BannerContentType.ImageOnly) {
                            mainContainerRef.current.style.cursor = 'pointer';
                        }
                    }
                }
                onMouseLeave={
                    () => {
                        if (mainContainerRef.current && viewData.contentType != BannerContentType.ImageOnly) {
                            mainContainerRef.current.style.cursor = 'auto';
                        }
                    }
                }
            >
                {
                    viewData.isWideImage
                        ? <Stack
                            width="100%"
                            height="100%"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <BackgroundImage
                                imageUrl={viewData.imageUrl}
                            />
                            {
                                viewData.panoramicOption == BannerPanoramicOption.AlignLeft
                                    ? <Box sx={{ width: "100%", height: `calc(100% - ${BANNER_CONTENT_HEIGHT}px)`, overflow: "hidden", position: "absolute", top: 0 }}>
                                        <img
                                            src={viewData.imageUrl}
                                            style={{
                                                backgroundImage: viewData.imageUrl,
                                                borderRadius: "16px,0px,16px,0px",
                                                objectPosition: "top left",
                                                objectFit: "cover",
                                                height: viewData.imageHeight > viewData.containerHeight ? "100%" : viewData.imageHeight,
                                            }}
                                        />
                                    </Box>
                                    : <img
                                        src={viewData.imageUrl}
                                        style={{
                                            backgroundImage: viewData.imageUrl,
                                            position: "absolute",
                                            top: 0,
                                            borderRadius: "16px,0px,16px,0px",
                                            margin: 0,
                                            objectFit: viewData.panoramicOption == BannerPanoramicOption.AlignCenter ? "fill" : "contain",
                                            maxWidth: viewData.panoramicOption == BannerPanoramicOption.AlignCenter ? "unset" : "100%",
                                            maxHeight: `calc(100% - ${BANNER_CONTENT_HEIGHT}px)`,
                                            height: viewData.containerHeight,
                                        }}
                                    />
                            }
                        </Stack>
                        : (
                            <>
                                <BackgroundImage
                                    imageUrl={viewData.imageUrl}
                                />

                                <LateralContainer>
                                    <img
                                        src={viewData.imageUrl}
                                        style={{
                                            backgroundImage: viewData.imageUrl,
                                            right: 0,
                                            bottom: 0,
                                            boxShadow: "3px 2px 16px rgba(0, 0, 0, 0.7)",
                                            position: "absolute",
                                            objectFit: "contain",
                                            borderRadius: "16px",
                                            maxHeight: "100%",
                                            maxWidth: "100%"
                                        }}
                                    />
                                </LateralContainer>
                            </>
                        )
                }

                <ContentContainer>
                    <Grid container flexWrap="nowrap">
                        <Grid size="grow" paddingX="16px">
                            <Grid container direction="column" flexWrap="nowrap">
                                <Grid>
                                    <Grid style={{ paddingTop: "16px" }}>
                                        <Typography
                                            variant="captionHeader"
                                            fontWeight="bold"
                                            component="p"
                                            color="warning.main"
                                            noWrap
                                        >
                                            <b>{viewData.headLine}</b>
                                        </Typography>
                                    </Grid>
                                    <Grid size="grow">
                                        <Typography
                                            variant="h3"
                                            fontWeight="bold"
                                            color="notice.dark"
                                            noWrap
                                        >
                                            {viewData.title}
                                        </Typography>
                                    </Grid>
                                    {
                                        viewData.subTitle &&
                                        <Grid>
                                            <Typography
                                                variant="caption"
                                                fontWeight="bold"
                                                component="p"
                                                noWrap
                                            >
                                                <b>{viewData.subTitle}</b>
                                            </Typography>
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </ContentContainer>
            </MainContainer >
        </>
    );
};
